import type { ObjectId } from 'mongodb';

import { MilestoneRequirement } from './Milestones';
import { RequirementFlagSettingsType } from './RequirementFlagSettings';

export enum InstallDocumentTypes {
  dxf = 'dxf',
  planSet = 'planSet',
  shadeReport = 'shadeReport',
  permit = 'permit',
  productionModel = 'productionModel',
  incentiveDocuments = 'incentiveDocuments',
  electricianSignOff = 'electricianSignOff', // too bad this is an enum, it makes this optional member harder to express
  proofOfLoad = 'proofOfLoad',
  domesticContentDocuments = 'domesticContentDocuments',
  solarReadinessAdder = 'solarReadinessAdder',
  solarReadinessInvoice = 'solarReadinessInvoice',
  invoice = 'invoice',
  billOfMaterials = 'billOfMaterials',
  fsmChecklist = 'fsmChecklist',
  measureQuickSystemVitals = 'measureQuickSystemVitals',
  bluetoothHVACTool = 'bluetoothHVACTool',
  installContract = 'installContract',
  proofOfLoadHVAC = 'proofOfLoadHVAC',
  hvacInstallationPhotos = 'hvacInstallationPhotos',
  ahriCertificate = 'ahriCertificate',
  energyBackupInstallationPhotos = 'energyBackupInstallationPhotos',
  cslbDisclosure = 'cslbDisclosure',
  puertoRicoDocuments = 'puertoRicoDocuments',
  welcomeCall = 'welcomeCall',
  onSitePhysicalSurvey = 'onSitePhysicalSurvey',
  proofOfEquipment = 'proofOfEquipment',
  invoiceOrBom = 'invoiceOrBom',
}

export enum InstallationPackagePhotoCategory {
  projectSite = 'projectSite',
  roof = 'roof',
  electrical = 'electrical',
  storage = 'storage',
  systemCommissioning = 'systemCommissioning',
  staticPressure = 'staticPressure',
  indoorSystem = 'indoorSystem',
  outdoorSystem = 'outdoorSystem',
  zoningSystem = 'zoningSystem',
  thermostat = 'thermostat',
  accessories = 'accessories',
  hvacInstallationPhotos = 'hvacInstallationPhotos',
  energyBackupInstallationPhotos = 'energyBackupInstallationPhotos',
  waterHeaterInstallationPhotos = 'waterHeaterInstallationPhotos',
  waterHeaterSystem = 'waterHeaterSystem',
  equipmentDataPlate = 'equipmentDataPlate',
  wideInstallationView = 'wideInstallationView',
  tpReliefValve = 'tpReliefValve',
  drainPanDrainLine = 'drainPanDrainLine',
  generatorInstallationPhotos = 'generatorInstallationPhotos',
  transferSwitch = 'transferSwitch',
  fuelConnection = 'fuelConnection',
  remoteMonitoringSetup = 'remoteMonitoringSetup',
  cellularDeviceId = 'cellularDeviceId',
  mainServicePanel = 'mainServicePanel',
  loadControlDevices = 'loadControlDevices',
  fieldProFuelLinePressure = 'fieldProFuelLinePressure',
  fieldProSystemRuntime = 'fieldProSystemRuntime',
  geothermalInstallationPhotos = 'geothermalInstallationPhotos',
  mechanicalRoomWideShot = 'mechanicalRoomWideShot',
  flowCenter = 'flowCenter',
  loopFieldPhoto = 'loopFieldPhoto',
  electricalDisconnect = 'electricalDisconnect',
}

export enum BaseInstallPackageFileCategory {
  permit = 'permit',
  utilityBill = 'utilityBill',
  incentives = 'incentives',
  planSet = 'planSet',
  productionModel = 'productionModel',
  shadeReport = 'shadeReport',
  projectSite = 'projectSite',
  roof = 'roof',
  electrical = 'electrical',
  storage = 'storage',
  systemCommissioning = 'systemCommissioning',
  interconnectionAgreement = 'interconnectionAgreement',
  disclosure = 'disclosure',
  cslbDisclosure = 'cslbDisclosure',
  puertoRicoDocuments = 'puertoRicoDocuments',
  welcomeCall = 'welcomeCall',
  onSitePhysicalSurvey = 'onSitePhysicalSurvey',
  proofOfEquipment = 'proofOfEquipment',
}

export enum DCInstallPackageFileCategory {
  domesticContentDocuments = 'domesticContentDocuments',
  invoice = 'invoice',
  billOfMaterials = 'billOfMaterials',
  incomingDirectPayInvoice = 'incomingDirectPayInvoice',
}

export enum DoePRInstallPackageFileCategory {
  proofOfLoad = 'proofOfLoad',
  electricianSignOff = 'electricianSignOff',
  solarReadinessAdder = 'solarReadinessAdder',
}

export enum HVACInstallPackageFileCategory {
  hvacInstallationPhotos = 'hvacInstallationPhotos',
  installContract = 'installContract',
  proofOfLoadHVAC = 'proofOfLoadHVAC',
  fsmChecklist = 'fsmChecklist',
  measureQuickSystemVitals = 'measureQuickSystemVitals',
  bluetoothHVACTool = 'bluetoothHVACTool',
  projectSite = 'projectSite',
  staticPressure = 'staticPressure',
  indoorSystem = 'indoorSystem',
  outdoorSystem = 'outdoorSystem',
  zoningSystem = 'zoningSystem',
  thermostat = 'thermostat',
  accessories = 'accessories',
  ahriCertificate = 'ahriCertificate',
  oemWarrantyDocuments = 'oemWarrantyDocuments',
  extendedWarrantyDocuments = 'extendedWarrantyDocuments',
}

export enum GeothermalInstallPackageFileCategory {
  geothermalInstallationPhotos = 'geothermalInstallationPhotos',
  equipmentDataPlate = 'equipmentDataPlate',
  mechanicalRoomWideShot = 'mechanicalRoomWideShot',
  flowCenter = 'flowCenter',
  loopFieldPhoto = 'loopFieldPhoto',
  electricalDisconnect = 'electricalDisconnect',
  thermostat = 'thermostat',
  installContract = 'installContract',
  ahriCertificate = 'ahriCertificate',
  oemWarrantyDocuments = 'oemWarrantyDocuments',
  extendedWarrantyDocuments = 'extendedWarrantyDocuments',
}

export enum WaterHeaterInstallPackageFileCategory {
  waterHeaterInstallationPhotos = 'waterHeaterInstallationPhotos',
  waterHeaterSystem = 'waterHeaterSystem',
  installContract = 'installContract',
  equipmentDataPlate = 'equipmentDataPlate',
  wideInstallationView = 'wideInstallationView',
  tpReliefValve = 'tpReliefValve',
  drainPanDrainLine = 'drainPanDrainLine',
  ahriCertificate = 'ahriCertificate',
  oemWarrantyDocuments = 'oemWarrantyDocuments',
  extendedWarrantyDocuments = 'extendedWarrantyDocuments',
}

export enum GeneratorInstallPackageFileCategory {
  generatorInstallationPhotos = 'generatorInstallationPhotos',
  equipmentDataPlate = 'equipmentDataPlate',
  wideInstallationView = 'wideInstallationView',
  transferSwitch = 'transferSwitch',
  fuelConnection = 'fuelConnection',
  remoteMonitoringSetup = 'remoteMonitoringSetup',
  cellularDeviceId = 'cellularDeviceId',
  mainServicePanel = 'mainServicePanel',
  loadControlDevices = 'loadControlDevices',
  fieldProFuelLinePressure = 'fieldProFuelLinePressure',
  fieldProSystemRuntime = 'fieldProSystemRuntime',
  oemWarrantyDocuments = 'oemWarrantyDocuments',
  extendedWarrantyDocuments = 'extendedWarrantyDocuments',
}

export type InstallPackageFileCategory =
  | BaseInstallPackageFileCategory
  | DCInstallPackageFileCategory
  | DoePRInstallPackageFileCategory
  | HVACInstallPackageFileCategory
  | WaterHeaterInstallPackageFileCategory
  | GeneratorInstallPackageFileCategory
  | GeothermalInstallPackageFileCategory;

export enum BaseActivationPackageFileCategory {
  ptoConfirmation = 'ptoConfirmation',
  utilityBill = 'utilityBill',
  interconnectionAgreement = 'interconnectionAgreement',
  incentives = 'incentives',
  extendedWarranty = 'extendedWarranty',
  siteImprovements = 'siteImprovements',
  disclosure = 'disclosure',
  cslbDisclosure = 'cslbDisclosure',
  puertoRicoDocuments = 'puertoRicoDocuments',
  welcomeCall = 'welcomeCall',
  onSitePhysicalSurvey = 'onSitePhysicalSurvey',
  proofOfEquipment = 'proofOfEquipment',
}

export enum ReviewPackagePhotoCategory {
  projectSite = 'projectSite',
  roof = 'roof',
  electrical = 'electrical',
  storage = 'storage',
  systemCommissioning = 'systemCommissioning',
  siteImprovements = 'siteImprovements',
}

export enum LegacyActivationDocumentTypes {
  ptoConfirmation = 'ptoConfirmation',
  extendedWarranty = 'extendedWarranty',
  incentives = 'incentives',
}

export enum ActivationDocumentTypes {
  ptoConfirmation = 'ptoConfirmation',
  extendedWarranty = 'extendedWarranty',
  incentives = 'incentives',
  netMeteringApproval = 'netMeteringApproval',
  conditionalApproval = 'conditionalApproval',
  electricianSignOff = 'electricianSignOff',
  disclosure = 'disclosure',
  cslbDisclosure = 'cslbDisclosure',
  interconnectionAgreement = 'interconnectionAgreement',
  puertoRicoDocuments = 'puertoRicoDocuments',
  welcomeCall = 'welcomeCall',
  onSitePhysicalSurvey = 'onSitePhysicalSurvey',
  proofOfEquipment = 'proofOfEquipment',
}

export enum PostActivationDocumentTypes {
  ptoConfirmation = 'ptoConfirmation',
}

export enum BillingDocumentTypes {
  incomingDirectPayInvoice = 'incomingDirectPayInvoice',
}

export enum ActivationPackagePhotoCategory {
  siteImprovements = 'siteImprovements',
}

export enum VerifyExistingSystemDocumentTypes {
  existingSystemPhotos = 'existingSystemPhotos',
}

export enum DocumentTypes {
  contract = 'contract',
  amendment = 'amendment',
  disclosure = 'disclosure',
  supporting = 'supporting',
  utilityBill = 'utilityBill',
  voidedCheck = 'voidedCheck',
  siteAssessment = 'siteAssessment',
  completionOfConstruction = 'completionOfConstruction',
  puertoRicoDocuments = 'puertoRicoDocuments',
  welcomeCall = 'welcomeCall',
  onSitePhysicalSurvey = 'onSitePhysicalSurvey',
  proofOfEquipment = 'proofOfEquipment',
  executedEscrowAgreement = 'executedEscrowAgreement',
  checklistSnapshot = 'checklistSnapshot',
}

export enum InboundPaymentsAccountStatusDocumentTypes {
  concordStatement = 'concordStatement',
}

// documents that are no longer uploaded, but may still be in the system
export enum DeprecatedDocumentTypes {
  employmentVerification = 'employmentVerification',
  incomeVerification = 'incomeVerification',
  creditReport = 'creditReport',
}

export enum InterconnectionAgreementDocumentType {
  interconnectionAgreement = 'interconnectionAgreement',
}

export const InstallPackageDocumentTypes: (
  | InstallDocumentTypes
  | ActivationDocumentTypes
  | DocumentTypes
  | InstallationPackagePhotoCategory
  | GeothermalInstallPackageFileCategory
)[] = [
  InstallDocumentTypes.planSet,
  InstallDocumentTypes.productionModel,
  InstallDocumentTypes.shadeReport,
  InstallDocumentTypes.permit,
  ActivationDocumentTypes.extendedWarranty,
  ActivationDocumentTypes.incentives,
  DocumentTypes.utilityBill,
  InstallationPackagePhotoCategory.projectSite,
  InstallationPackagePhotoCategory.roof,
  InstallationPackagePhotoCategory.electrical,
  InstallationPackagePhotoCategory.storage,
  InstallationPackagePhotoCategory.systemCommissioning,
  GeothermalInstallPackageFileCategory.geothermalInstallationPhotos,
  GeothermalInstallPackageFileCategory.equipmentDataPlate,
  GeothermalInstallPackageFileCategory.mechanicalRoomWideShot,
  GeothermalInstallPackageFileCategory.flowCenter,
  GeothermalInstallPackageFileCategory.loopFieldPhoto,
  GeothermalInstallPackageFileCategory.electricalDisconnect,
  GeothermalInstallPackageFileCategory.thermostat,
  GeothermalInstallPackageFileCategory.installContract,
  GeothermalInstallPackageFileCategory.ahriCertificate,
  GeothermalInstallPackageFileCategory.oemWarrantyDocuments,
  GeothermalInstallPackageFileCategory.extendedWarrantyDocuments,
];

export const ActivationPackageDocumentTypes: (
  | ActivationDocumentTypes
  | DocumentTypes
  | ActivationPackagePhotoCategory
)[] = [
  ActivationDocumentTypes.ptoConfirmation,
  ActivationDocumentTypes.extendedWarranty,
  ActivationDocumentTypes.incentives,
  DocumentTypes.utilityBill,
  ActivationPackagePhotoCategory.siteImprovements,
  ActivationDocumentTypes.netMeteringApproval,
  ActivationDocumentTypes.disclosure,
  ActivationDocumentTypes.cslbDisclosure,
  ActivationDocumentTypes.interconnectionAgreement,
  ActivationDocumentTypes.puertoRicoDocuments,
  ActivationDocumentTypes.welcomeCall,
  ActivationDocumentTypes.onSitePhysicalSurvey,
  ActivationDocumentTypes.proofOfEquipment,
];

export const PostActivationPackageDocumentTypes: ActivationDocumentTypes[] = [
  ActivationDocumentTypes.ptoConfirmation,
];

export enum UnderwritingDocumentTypes {
  identityVerification = 'identityVerification',
  titleVerification = 'titleVerification',
}

export enum PricingDocumentType {
  priceSheet = 'priceSheet',
}

export enum CancellationDocumentTypes {
  cancellation = 'cancellation',
  reactivation = 'reactivation',
}

export enum FlagDocumentTypes {
  flag = 'flag',
  epcFeedback = 'epcFeedback',
}

export enum HVACDocumentTypes {
  salesAgreement = 'salesAgreement',
  /** @deprecated This is now part of the install package */
  oemWarranty = 'oemWarranty',
  extendedWarranty = 'extendedWarranty',
}

export enum WaterHeaterDocumentTypes {
  salesAgreement = 'salesAgreement',
  /** @deprecated This is now part of the install package */
  oemWarranty = 'oemWarranty',
  extendedWarranty = 'extendedWarranty',
}

export enum GeneratorDocumentTypes {
  salesAgreement = 'salesAgreement',
  /** @deprecated This is now part of the install package */
  oemWarranty = 'oemWarranty',
  extendedWarranty = 'extendedWarranty',
}

export enum GeothermalDocumentTypes {
  salesAgreement = 'salesAgreement',
  oemWarranty = 'oemWarranty',
  extendedWarranty = 'extendedWarranty',
}

export enum GeothermalDesignDocumentTypes {
  manualJ = 'manualJ',
  manualS = 'manualS',
  loopDesignReport = 'loopDesignReport',
  soilGeologyReport = 'soilGeologyReport',
}

export enum GeothermalLoopFieldDocumentTypes {
  loopFieldDocumentation = 'loopFieldDocumentation',
  pressureTestResults = 'pressureTestResults',
  loopInstallationPhotos = 'loopInstallationPhotos',
  drillingInvoice = 'drillingInvoice',
  boreLog = 'boreLog',
}

export enum DoePrDocumentTypes {
  siteAssessment = 'siteAssessment',
}

export const LoopFieldPackageDocumentTypes: (GeothermalLoopFieldDocumentTypes | GeothermalDesignDocumentTypes)[] = [
  GeothermalLoopFieldDocumentTypes.loopFieldDocumentation,
  GeothermalLoopFieldDocumentTypes.pressureTestResults,
  GeothermalLoopFieldDocumentTypes.loopInstallationPhotos,
  GeothermalLoopFieldDocumentTypes.drillingInvoice,
  GeothermalLoopFieldDocumentTypes.boreLog,
  GeothermalDesignDocumentTypes.manualJ,
  GeothermalDesignDocumentTypes.manualS,
  GeothermalDesignDocumentTypes.loopDesignReport,
  GeothermalDesignDocumentTypes.soilGeologyReport,
];

export type AllDocumentTypes =
  | DocumentTypes
  | InstallDocumentTypes
  | InstallationPackagePhotoCategory
  | ActivationDocumentTypes
  | PostActivationDocumentTypes
  | ActivationPackagePhotoCategory
  | UnderwritingDocumentTypes
  | PricingDocumentType
  | CancellationDocumentTypes
  | BillingDocumentTypes
  | DeprecatedDocumentTypes
  | InterconnectionAgreementDocumentType
  | FlagDocumentTypes
  | HVACDocumentTypes
  | WaterHeaterDocumentTypes
  | GeneratorDocumentTypes
  | GeothermalDocumentTypes
  | GeothermalDesignDocumentTypes
  | GeothermalLoopFieldDocumentTypes
  | DoePrDocumentTypes
  | InstallPackageFileCategory
  | VerifyExistingSystemDocumentTypes;

export enum DocumentStatus {
  pending = 'pending',
  approved = 'approved',
  rejected = 'rejected',
  voided = 'voided',
}

export enum ViewUrlType {
  original = 'original',
  preview = 'preview',
  gallery = 'gallery',
  thumbnail = 'thumbnail',
}

export interface FileVariant {
  type: ViewUrlType;
  filePath: string;
  width?: number;
  height?: number;
}

export interface DocumentReference {
  type: string;
  id: string | number | ObjectId;
}

export interface DocumentFile {
  originalName: string;
  contentType: string;
  sizeKB: number;
  md5Hash: string;
  filePath: string;
  variants?: FileVariant[];
  variantStatus?: 'pending' | 'processing' | 'complete' | 'failed';
  viewUrls?: {
    url: string;
    type: ViewUrlType;
    expiresAt?: string;
  }[];
}

export interface DocumentUploadFields {
  type: keyof typeof DocumentTypes | PricingDocumentType;
  externalReference?: string | number;
  internalReferences?: { fieldname: string; value: string }[];
  grouped?: string;
  name?: string;
  isReviewable?: string;
  context?: string;
}

export interface DocumentDocument {
  id?: ObjectId | string;
  accountId?: ObjectId | string;
  applicationId?: ObjectId | string;
  archived: boolean;
  type: AllDocumentTypes;
  status: keyof typeof DocumentStatus;
  internalReferences?: DocumentReference[];
  externalReference?: string | number;
  files: DocumentFile[];
  name?: string;
  /**
   * Internal — set when the document was created via a v2 signed-URL
   * upload job. Used by variant processors to look up sibling documents
   * for the `imageVariantsReady` event payload. NOT mirrored on
   * `DocumentApiResponse`.
   */
  documentUploadJobId?: string;
  meta?: {
    createdAt: string;
  };
}

export interface DocumentFileApiResponse extends DocumentFile {
  viewUrls: {
    url: string;
    type: ViewUrlType;
    expiresAt?: string;
  }[];
}
export interface DocumentApiResponse extends DocumentDocument {
  files: DocumentFileApiResponse[];
}

export const InstallationPackagePhotoCategoryArr: InstallationPackagePhotoCategory[] = Object.values(
  InstallationPackagePhotoCategory,
);

export const InstallDocumentTypesArr: InstallDocumentTypes[] = Object.values(InstallDocumentTypes);
export const ActivationDocumentTypesArr: ActivationDocumentTypes[] = Object.values(ActivationDocumentTypes);
export const ActivationPackagePhotoCategoryArr: ActivationPackagePhotoCategory[] = Object.values(
  ActivationPackagePhotoCategory,
);
export const DocumentTypesArr: DocumentTypes[] = Object.values(DocumentTypes);
export const DeprecatedDocumentTypesArr: DeprecatedDocumentTypes[] = Object.values(DeprecatedDocumentTypes);
export const UnderwritingDocumentTypesArr: UnderwritingDocumentTypes[] = Object.values(UnderwritingDocumentTypes);
export const HVACDocumentTypesArr: HVACDocumentTypes[] = Object.values(HVACDocumentTypes);
export const WaterHeaterDocumentTypesArr: WaterHeaterDocumentTypes[] = Object.values(WaterHeaterDocumentTypes);
export const GeneratorDocumentTypesArr: GeneratorDocumentTypes[] = Object.values(GeneratorDocumentTypes);
export const GeothermalDocumentTypesArr: GeothermalDocumentTypes[] = Object.values(GeothermalDocumentTypes);
export const GeothermalDesignDocumentTypesArr: GeothermalDesignDocumentTypes[] =
  Object.values(GeothermalDesignDocumentTypes);
export const DoePrDocumentTypesArr: DoePrDocumentTypes[] = Object.values(DoePrDocumentTypes);
export const AllDocumentTypesArr: AllDocumentTypes[] = [
  ...new Set<AllDocumentTypes>([
    ...Object.values(BillingDocumentTypes),
    ...Object.values(InstallDocumentTypes),
    ...Object.values(DocumentTypes),
    ...InstallationPackagePhotoCategoryArr,
    ...Object.values(ActivationDocumentTypes),
    ...Object.values(ActivationPackagePhotoCategory),
    ...Object.values(UnderwritingDocumentTypes),
    ...Object.values(CancellationDocumentTypes),
    ...Object.values(InterconnectionAgreementDocumentType),
    ...Object.values(FlagDocumentTypes),
    ...Object.values(HVACDocumentTypes),
    ...Object.values(HVACInstallPackageFileCategory),
    ...Object.values(WaterHeaterDocumentTypes),
    ...Object.values(WaterHeaterInstallPackageFileCategory),
    ...Object.values(GeneratorDocumentTypes),
    ...Object.values(GeneratorInstallPackageFileCategory),
    ...Object.values(GeothermalDocumentTypes),
    ...Object.values(GeothermalDesignDocumentTypes),
    ...Object.values(GeothermalLoopFieldDocumentTypes),
    ...Object.values(GeothermalInstallPackageFileCategory),
    ...Object.values(DoePrDocumentTypes),
    ...Object.values(VerifyExistingSystemDocumentTypes),
  ]),
];
export const SeparateDocumentTypes: (
  | InstallationPackagePhotoCategory
  | ActivationPackagePhotoCategory
  | InstallDocumentTypes
  | GeothermalLoopFieldDocumentTypes
)[] = [
  ...new Set<InstallationPackagePhotoCategory | ActivationPackagePhotoCategory>([
    ...InstallationPackagePhotoCategoryArr,
    ...Object.values(ActivationPackagePhotoCategory),
  ]),
  InstallDocumentTypes.ahriCertificate,
  GeothermalLoopFieldDocumentTypes.loopFieldDocumentation,
];

export const documentTitles: Record<AllDocumentTypes, string> = {
  contract: 'Contract',
  amendment: 'Amendment',
  creditReport: 'Credit Report',
  disclosure: 'Disclosure',
  domesticContentDocuments: 'Domestic Content Documents',
  dxf: 'DXF',
  employmentVerification: 'Employment Verification',
  identityVerification: 'Identity Verification',
  incomeVerification: 'Income Verification',
  incomingDirectPayInvoice: 'Incoming Direct Pay Invoice',
  permit: 'Permit',
  planSet: 'Plan Set',
  productionModel: 'Production Model',
  shadeReport: 'Shade Report',
  supporting: 'Supporting',
  titleVerification: 'Title Verification',
  utilityBill: 'Utility Bill',
  projectSite: 'Project Site',
  roof: 'Roof',
  electrical: 'Electrical',
  storage: 'Storage',
  systemCommissioning: 'System Commissioning',
  voidedCheck: 'Voided Check',
  ptoConfirmation: 'PTO Confirmation',
  oemWarranty: 'OEM Warranty',
  extendedWarranty: 'Extended Warranty',
  incentives: 'Incentives',
  siteImprovements: 'Site Improvements',
  priceSheet: 'Price Sheet',
  cancellation: 'Cancellation',
  reactivation: 'Reactivation',
  netMeteringApproval: 'Net Metering Approval',
  interconnectionAgreement: 'Interconnection Agreement',
  flag: 'Flag',
  incentiveDocuments: 'Incentive Documents',
  conditionalApproval: 'Conditional Approval',
  electricianSignOff: 'Electrician Sign-Off',
  proofOfLoad: 'Proof of Load',
  installContract: 'Install Contract',
  staticPressure: 'Static Pressure',
  salesAgreement: 'Sales Agreement',
  solarReadinessAdder: 'Solar Readiness Adder',
  solarReadinessInvoice: 'Solar Readiness Invoice',
  invoice: 'Invoice',
  billOfMaterials: 'Bill of Materials',
  siteAssessment: 'Site Assessment',
  fsmChecklist: 'FSM Checklist',
  measureQuickSystemVitals: 'Measure Quick System Vitals',
  bluetoothHVACTool: 'Bluetooth HVAC Tool',
  indoorSystem: 'Indoor System',
  outdoorSystem: 'Outdoor System',
  zoningSystem: 'Zoning System',
  thermostat: 'Thermostat',
  accessories: 'Accessories',
  proofOfLoadHVAC: 'Proof of Load',
  epcFeedback: 'EPC Feedback',
  hvacInstallationPhotos: 'HVAC Installation Photos',
  waterHeaterInstallationPhotos: 'Water Heater Installation Photos',
  waterHeaterSystem: 'Water Heater System',
  generatorInstallationPhotos: 'Generator Installation Photos',
  transferSwitch: 'Transfer Switch / ATS',
  fuelConnection: 'Fuel Connection',
  remoteMonitoringSetup: 'Remote Monitoring Setup',
  cellularDeviceId: 'Cellular Device ID',
  mainServicePanel: 'Main Service Panel',
  loadControlDevices: 'Load Control Devices',
  fieldProFuelLinePressure: 'FieldPro Screenshots: Fuel Line Pressure Test',
  fieldProSystemRuntime: 'FieldPro Screenshots: System Runtime',
  equipmentDataPlate: 'Equipment Data Plate',
  wideInstallationView: 'Wide Installation View',
  tpReliefValve: 'Temperature & Pressure (T&P) Relief Valve',
  drainPanDrainLine: 'Drain Pan & Drain Line',
  ahriCertificate: 'AHRI Certificate',
  oemWarrantyDocuments: 'OEM Warranty Documents',
  extendedWarrantyDocuments: 'Extended Warranty Documents',
  energyBackupInstallationPhotos: 'Energy Backup Installation Photos',
  existingSystemPhotos: 'Existing System Photos',
  geothermalInstallationPhotos: 'Geothermal Installation Photos',
  mechanicalRoomWideShot: 'Mechanical Room Wide Shot',
  flowCenter: 'Flow Center',
  loopFieldPhoto: 'Loop Field Photo',
  electricalDisconnect: 'Electrical Disconnect',
  manualJ: 'Manual J (Load Calculation)',
  manualS: 'Manual S (Equipment Selection)',
  loopDesignReport: 'Loop Design Report',
  soilGeologyReport: 'Soil / Geology Report',
  loopFieldDocumentation: 'Loop Field Documentation',
  pressureTestResults: 'Pressure Test Results',
  loopInstallationPhotos: 'Loop Installation Photos',
  drillingInvoice: 'Drilling Invoice',
  boreLog: 'Bore Log',
  completionOfConstruction: 'Completion of Construction',
  cslbDisclosure: 'CSLB Disclosure (CA Only)',
  puertoRicoDocuments: 'Puerto Rico Documents',
  welcomeCall: 'Welcome Call',
  onSitePhysicalSurvey: 'On-Site Physical Survey',
  proofOfEquipment: 'Proof of Equipment',
  invoiceOrBom: 'Invoice/Bill of Materials',
  executedEscrowAgreement: 'Executed Escrow Agreement',
  checklistSnapshot: 'QC Review Record',
};

export const documentRequirementMap: { [Key in AllDocumentTypes]?: MilestoneRequirement } = {
  [InstallDocumentTypes.dxf]: MilestoneRequirement.dxfApproved,
  [InstallDocumentTypes.permit]: MilestoneRequirement.permitApproved,
  [InstallDocumentTypes.incentiveDocuments]: MilestoneRequirement.incentiveDocumentsApproved,
  [InstallDocumentTypes.planSet]: MilestoneRequirement.planSetApproved,
  [InstallDocumentTypes.productionModel]: MilestoneRequirement.productionModelApproved,
  [InstallDocumentTypes.shadeReport]: MilestoneRequirement.shadeReportApproved,
  [InstallDocumentTypes.proofOfLoad]: MilestoneRequirement.proofOfLoad,
  [InstallationPackagePhotoCategory.electrical]: MilestoneRequirement.electricalPhotosApproved,
  [InstallationPackagePhotoCategory.projectSite]: MilestoneRequirement.projectSitePhotosApproved,
  [InstallationPackagePhotoCategory.roof]: MilestoneRequirement.roofPhotosApproved,
  [InstallationPackagePhotoCategory.storage]: MilestoneRequirement.storagePhotosApproved,
  [InstallationPackagePhotoCategory.systemCommissioning]: MilestoneRequirement.systemCommissioningApproved,
  [InstallationPackagePhotoCategory.staticPressure]: MilestoneRequirement.staticPressureReadings,
  [InstallationPackagePhotoCategory.indoorSystem]: MilestoneRequirement.indoorSystemApproved,
  [InstallationPackagePhotoCategory.outdoorSystem]: MilestoneRequirement.outdoorSystemApproved,
  [InstallationPackagePhotoCategory.zoningSystem]: MilestoneRequirement.zoningSystemApproved,
  [InstallationPackagePhotoCategory.thermostat]: MilestoneRequirement.thermostatApproved,
  [InstallationPackagePhotoCategory.accessories]: MilestoneRequirement.accessoriesApproved,
  [ActivationDocumentTypes.extendedWarranty]: MilestoneRequirement.extendedWarranty,
  [ActivationDocumentTypes.incentives]: MilestoneRequirement.incentives,
  [ActivationDocumentTypes.ptoConfirmation]: MilestoneRequirement.ptoConfirmation,
  [GeothermalDesignDocumentTypes.manualJ]: MilestoneRequirement.loopFieldDesignDocumentsApproved,
  [GeothermalDesignDocumentTypes.manualS]: MilestoneRequirement.loopFieldDesignDocumentsApproved,
  [GeothermalDesignDocumentTypes.loopDesignReport]: MilestoneRequirement.loopFieldDesignDocumentsApproved,
  [GeothermalDesignDocumentTypes.soilGeologyReport]: MilestoneRequirement.loopFieldDesignDocumentsApproved,
  [ActivationPackagePhotoCategory.siteImprovements]: MilestoneRequirement.siteImprovements,
  [UnderwritingDocumentTypes.identityVerification]: MilestoneRequirement.identityVerified,
  [UnderwritingDocumentTypes.titleVerification]: MilestoneRequirement.titleApproved,
  [DocumentTypes.voidedCheck]: MilestoneRequirement.paymentMethodSubmitted,
  [InstallDocumentTypes.fsmChecklist]: MilestoneRequirement.fsmChecklistApproved,
  [InstallDocumentTypes.measureQuickSystemVitals]: MilestoneRequirement.measureQuickSystemVitalsApproved,
  [InstallDocumentTypes.bluetoothHVACTool]: MilestoneRequirement.bluetoothHVACToolApproved,
  [DocumentTypes.utilityBill]: MilestoneRequirement.utilityBill,
};

export const documentTypeToRequirementFlagSettingsMap: { [Key in AllDocumentTypes]?: RequirementFlagSettingsType } =
  {
    [BillingDocumentTypes.incomingDirectPayInvoice]: RequirementFlagSettingsType.incomingDirectPayInvoice,
    [DocumentTypes.utilityBill]: RequirementFlagSettingsType.utilityBill,
    [DocumentTypes.contract]: RequirementFlagSettingsType.customerContract,
    [InterconnectionAgreementDocumentType.interconnectionAgreement]:
      RequirementFlagSettingsType.approvedInterconnectionAgreement,
    [InstallDocumentTypes.permit]: RequirementFlagSettingsType.approvedPermit,
    [InstallDocumentTypes.planSet]: RequirementFlagSettingsType.designPlanSet,
    [InstallDocumentTypes.productionModel]: RequirementFlagSettingsType.designProductionModel,
    [InstallDocumentTypes.shadeReport]: RequirementFlagSettingsType.designShadeReport,
    [InstallDocumentTypes.proofOfLoad]: RequirementFlagSettingsType.proofOfLoadCalculation,
    [InstallDocumentTypes.domesticContentDocuments]: RequirementFlagSettingsType.domesticContentDocuments,
    [InstallDocumentTypes.invoice]: RequirementFlagSettingsType.invoice,
    [InstallDocumentTypes.billOfMaterials]: RequirementFlagSettingsType.billOfMaterials,
    [InstallDocumentTypes.ahriCertificate]: RequirementFlagSettingsType.ahriCertificate,
    [InstallationPackagePhotoCategory.electrical]: RequirementFlagSettingsType.electrical,
    [InstallationPackagePhotoCategory.projectSite]: RequirementFlagSettingsType.projectSite,
    [InstallationPackagePhotoCategory.roof]: RequirementFlagSettingsType.roof,
    [InstallationPackagePhotoCategory.storage]: RequirementFlagSettingsType.storage,
    [InstallationPackagePhotoCategory.systemCommissioning]: RequirementFlagSettingsType.systemCommissioning,
    [InstallationPackagePhotoCategory.staticPressure]: RequirementFlagSettingsType.staticPressureReadings,
    [InstallationPackagePhotoCategory.indoorSystem]: RequirementFlagSettingsType.indoorSystem,
    [InstallationPackagePhotoCategory.outdoorSystem]: RequirementFlagSettingsType.outdoorSystem,
    [InstallationPackagePhotoCategory.zoningSystem]: RequirementFlagSettingsType.zoningSystem,
    [InstallationPackagePhotoCategory.thermostat]: RequirementFlagSettingsType.thermostat,
    [InstallationPackagePhotoCategory.accessories]: RequirementFlagSettingsType.accessoriesHVAC,
    [ActivationDocumentTypes.conditionalApproval]: RequirementFlagSettingsType.conditionalApprovalRemedy,
    [ActivationDocumentTypes.extendedWarranty]: RequirementFlagSettingsType.proofOfExtendedWarranty,
    [ActivationDocumentTypes.incentives]: RequirementFlagSettingsType.incentive,
    [ActivationDocumentTypes.ptoConfirmation]: RequirementFlagSettingsType.proofOfPermissionToOperate,
    [ActivationDocumentTypes.netMeteringApproval]: RequirementFlagSettingsType.netMeteringApproval,
    [ActivationPackagePhotoCategory.siteImprovements]: RequirementFlagSettingsType.siteImprovement,
    [InstallDocumentTypes.electricianSignOff]: RequirementFlagSettingsType.electricianSignOff,
    [InstallDocumentTypes.fsmChecklist]: RequirementFlagSettingsType.fieldServiceManagementChecklistHVAC,
    [InstallDocumentTypes.measureQuickSystemVitals]: RequirementFlagSettingsType.measureSystemVitalsHVAC,
    [InstallDocumentTypes.bluetoothHVACTool]: RequirementFlagSettingsType.remoteDataLoggingReportHVAC,
    [InstallDocumentTypes.installContract]: RequirementFlagSettingsType.installContractHVAC,
    [InstallDocumentTypes.proofOfLoadHVAC]: RequirementFlagSettingsType.proofOfLoadCalculationHVAC,
    [VerifyExistingSystemDocumentTypes.existingSystemPhotos]: RequirementFlagSettingsType.existingSystemPhotos,
    [InstallationPackagePhotoCategory.energyBackupInstallationPhotos]:
      RequirementFlagSettingsType.energyBackupInstallationPhotos,
    [ActivationDocumentTypes.puertoRicoDocuments]: RequirementFlagSettingsType.puertoRicoDocuments,
    [ActivationDocumentTypes.welcomeCall]: RequirementFlagSettingsType.welcomeCall,
    [ActivationDocumentTypes.onSitePhysicalSurvey]: RequirementFlagSettingsType.onSitePhysicalSurvey,
    [ActivationDocumentTypes.proofOfEquipment]: RequirementFlagSettingsType.proofOfEquipment,
    [ActivationDocumentTypes.disclosure]: RequirementFlagSettingsType.disclosure,
    [ActivationDocumentTypes.cslbDisclosure]: RequirementFlagSettingsType.cslbDisclosure,
    [HVACInstallPackageFileCategory.oemWarrantyDocuments]: RequirementFlagSettingsType.oemWarrantyDocuments,
    [DocumentTypes.executedEscrowAgreement]: RequirementFlagSettingsType.executedEscrowAgreement,
  };

export enum DocumentSortField {
  ID_DESC = 'idDescending',
  ID_ASC = 'idAscending',
}

/**
 * Document sensitivity levels for determining URL access strategy.
 *
 * - high: Always stream through API proxy (contracts, financial docs, PII)
 * - medium: Signed URLs with short expiration (5 min) - utility bills, permits
 * - low: Signed URLs with standard expiration (15 min) - photos, technical docs
 */
export enum DocumentSensitivity {
  high = 'high',
  medium = 'medium',
  low = 'low',
}

/**
 * Internal mapping of document types to sensitivity levels.
 * Each unique string value from AllDocumentTypes is mapped exactly once.
 */
const documentTypeSensitivityMap: Record<string, DocumentSensitivity> = {
  // HIGH SENSITIVITY - Always stream through API
  [DocumentTypes.voidedCheck]: DocumentSensitivity.high,
  [BillingDocumentTypes.incomingDirectPayInvoice]: DocumentSensitivity.high,
  [UnderwritingDocumentTypes.identityVerification]: DocumentSensitivity.high,
  [UnderwritingDocumentTypes.titleVerification]: DocumentSensitivity.high,
  [DeprecatedDocumentTypes.creditReport]: DocumentSensitivity.high,
  [DeprecatedDocumentTypes.incomeVerification]: DocumentSensitivity.high,
  [DeprecatedDocumentTypes.employmentVerification]: DocumentSensitivity.high,
  // Contracts and disclosures contain customer signatures and full PII —
  // streaming proxy ensures permission checks on every access.
  [DocumentTypes.contract]: DocumentSensitivity.high,
  [DocumentTypes.disclosure]: DocumentSensitivity.high,

  // MEDIUM SENSITIVITY - Signed URLs (5 min)
  [DocumentTypes.utilityBill]: DocumentSensitivity.medium,
  [InstallDocumentTypes.permit]: DocumentSensitivity.medium,
  [InterconnectionAgreementDocumentType.interconnectionAgreement]: DocumentSensitivity.medium,
  [ActivationDocumentTypes.ptoConfirmation]: DocumentSensitivity.medium,
  [ActivationDocumentTypes.netMeteringApproval]: DocumentSensitivity.medium,
  [ActivationDocumentTypes.conditionalApproval]: DocumentSensitivity.medium,
  [PricingDocumentType.priceSheet]: DocumentSensitivity.medium,
  [InstallDocumentTypes.invoice]: DocumentSensitivity.medium,
  [InstallDocumentTypes.incentiveDocuments]: DocumentSensitivity.medium,
  [ActivationDocumentTypes.incentives]: DocumentSensitivity.medium,
  [ActivationDocumentTypes.extendedWarranty]: DocumentSensitivity.medium,
  [HVACDocumentTypes.oemWarranty]: DocumentSensitivity.medium,
  [HVACInstallPackageFileCategory.oemWarrantyDocuments]: DocumentSensitivity.medium,
  [HVACInstallPackageFileCategory.extendedWarrantyDocuments]: DocumentSensitivity.medium,
  [DocumentTypes.puertoRicoDocuments]: DocumentSensitivity.medium,

  // LOW SENSITIVITY - Signed URLs (15 min)
  [InstallationPackagePhotoCategory.projectSite]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.roof]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.electrical]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.storage]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.systemCommissioning]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.staticPressure]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.indoorSystem]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.outdoorSystem]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.zoningSystem]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.thermostat]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.accessories]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.hvacInstallationPhotos]: DocumentSensitivity.low,
  [InstallationPackagePhotoCategory.energyBackupInstallationPhotos]: DocumentSensitivity.low,
  [ActivationPackagePhotoCategory.siteImprovements]: DocumentSensitivity.low,
  [VerifyExistingSystemDocumentTypes.existingSystemPhotos]: DocumentSensitivity.low,
  [InstallDocumentTypes.dxf]: DocumentSensitivity.low,
  [InstallDocumentTypes.planSet]: DocumentSensitivity.low,
  [InstallDocumentTypes.shadeReport]: DocumentSensitivity.low,
  [InstallDocumentTypes.productionModel]: DocumentSensitivity.low,
  [InstallDocumentTypes.billOfMaterials]: DocumentSensitivity.low,
  [InstallDocumentTypes.proofOfLoad]: DocumentSensitivity.low,
  [InstallDocumentTypes.electricianSignOff]: DocumentSensitivity.low,
  [InstallDocumentTypes.domesticContentDocuments]: DocumentSensitivity.low,
  [InstallDocumentTypes.solarReadinessAdder]: DocumentSensitivity.low,
  [InstallDocumentTypes.solarReadinessInvoice]: DocumentSensitivity.low,
  [InstallDocumentTypes.fsmChecklist]: DocumentSensitivity.low,
  [InstallDocumentTypes.measureQuickSystemVitals]: DocumentSensitivity.low,
  [InstallDocumentTypes.bluetoothHVACTool]: DocumentSensitivity.low,
  [InstallDocumentTypes.proofOfLoadHVAC]: DocumentSensitivity.low,
  [InstallDocumentTypes.ahriCertificate]: DocumentSensitivity.low,
  [InstallDocumentTypes.welcomeCall]: DocumentSensitivity.low,
  [InstallDocumentTypes.onSitePhysicalSurvey]: DocumentSensitivity.low,
  [InstallDocumentTypes.proofOfEquipment]: DocumentSensitivity.low,
  [DocumentTypes.siteAssessment]: DocumentSensitivity.low,
  [DocumentTypes.supporting]: DocumentSensitivity.low,
  [DocumentTypes.completionOfConstruction]: DocumentSensitivity.low,
  [FlagDocumentTypes.flag]: DocumentSensitivity.low,
  [FlagDocumentTypes.epcFeedback]: DocumentSensitivity.low,
  [CancellationDocumentTypes.cancellation]: DocumentSensitivity.low,
  [CancellationDocumentTypes.reactivation]: DocumentSensitivity.low,
  [InstallDocumentTypes.installContract]: DocumentSensitivity.low,
  [InstallDocumentTypes.cslbDisclosure]: DocumentSensitivity.low,
  // Contains customer signatures and full PII — same rationale as
  // `contract` / `disclosure`. v2 init rejects new salesAgreement uploads,
  // but v2 GET / GET-by-id still resolves view URLs through this map for
  // any existing v1-uploaded document.
  [HVACDocumentTypes.salesAgreement]: DocumentSensitivity.high,

  // Geothermal install package photos — install-site imagery, no PII.
  [GeothermalInstallPackageFileCategory.geothermalInstallationPhotos]: DocumentSensitivity.low,
  [GeothermalInstallPackageFileCategory.mechanicalRoomWideShot]: DocumentSensitivity.low,
  [GeothermalInstallPackageFileCategory.flowCenter]: DocumentSensitivity.low,
  [GeothermalInstallPackageFileCategory.loopFieldPhoto]: DocumentSensitivity.low,
  [GeothermalInstallPackageFileCategory.electricalDisconnect]: DocumentSensitivity.low,

  // Geothermal design documents — engineering calculations and site reports, no PII.
  [GeothermalDesignDocumentTypes.manualJ]: DocumentSensitivity.low,
  [GeothermalDesignDocumentTypes.manualS]: DocumentSensitivity.low,
  [GeothermalDesignDocumentTypes.loopDesignReport]: DocumentSensitivity.low,
  [GeothermalDesignDocumentTypes.soilGeologyReport]: DocumentSensitivity.low,

  // Geothermal loop field documents — install/test artifacts, no PII.
  [GeothermalLoopFieldDocumentTypes.pressureTestResults]: DocumentSensitivity.low,
  [GeothermalLoopFieldDocumentTypes.loopInstallationPhotos]: DocumentSensitivity.low,
  [GeothermalLoopFieldDocumentTypes.boreLog]: DocumentSensitivity.low,
  // Matches InstallDocumentTypes.invoice — MEDIUM for billing artifacts.
  [GeothermalLoopFieldDocumentTypes.drillingInvoice]: DocumentSensitivity.medium,
};

/**
 * Maps document types to their sensitivity level.
 * HIGH sensitivity documents (contracts, financial, PII) always use the
 * streaming proxy to ensure permission checks on every access.
 */
export const DocumentTypeSensitivity = documentTypeSensitivityMap as Record<AllDocumentTypes, DocumentSensitivity>;

/**
 * Gets the sensitivity level for a document type.
 * Returns HIGH sensitivity by default for any unclassified types (fail-safe).
 */
export const getDocumentSensitivity = (documentType: AllDocumentTypes): DocumentSensitivity => {
  return documentTypeSensitivityMap[documentType] ?? DocumentSensitivity.high;
};

export enum DocumentRejectionReasons {
  missingDocumentation = 'Missing Documentation',
  illegibleDocumentation = 'Illegible Documentation',
  incorrectDocumentation = 'Incorrect Documentation',
  other = 'Other',
}

export enum UtilityBillRejectionReasons {
  notAUtilityBill = 'Not a utility bill',
  addressMismatch = 'Address Mismatch',
  billOutdated = 'Bill Outdated',
  pastDueBalanceExceeds1000 = 'Past Due balance',
  other = 'Other',
}

export enum UtilityBillExtractionCheckFailureReason {
  notAUtilityBill = 'document is not a utility bill',
  overdueAmountMissing = 'could not confirm bill is current (overdue amount missing)',
  overdueAmountExceedsThreshold = 'overdue balance exceeds the auto-approval threshold',
  documentDateMissing = 'bill date could not be extracted',
  documentDateTooOld = 'bill date is more than 6 months old',
  noUtilityLseId = 'no utility provider on file for this account',
  utilityNotFound = 'utility provider not found in system',
  utilityExcludedFromAutoApproval = 'utility is excluded from auto-approval',
  allFieldsExcluded = 'all required fields are excluded by utility rules',
  fieldMismatch = 'extracted bill data does not match account information',
  fieldMismatchUtilityName = 'Utility name does not match account information',
  fieldMismatchServiceAddress = 'Service address does not match account information',
  fieldMismatchCustomerName = 'Customer name does not match account information',
  notElectricBill = 'bill does not include electric charges',
  utilityInactive = 'utility is not active',
  disconnectNoticePresent = 'bill shows an active disconnect notice',
}

export const documentsNotRequireReview: AllDocumentTypes[] = [
  DocumentTypes.utilityBill,
  DocumentTypes.checklistSnapshot,
];

export type DocumentRejectionMessageData = {
  customerName: string;
  epcName: string;
  customerAccountLink: string;
  customerAddress: string;
  ntpDocType: string;
};

export const InstallationPackagePhotoCategoryTitles: Record<string, string> = {
  projectSite: documentTitles.projectSite,
  roof: documentTitles.roof,
  electrical: documentTitles.electrical,
  storage: documentTitles.storage,
  systemCommissioning: documentTitles.systemCommissioning,
};
